/**
 * Renders one Ask AI chart spec (create_chart tool) with recharts.
 *
 * The backend sends data, never plotting code: this component is the only
 * plotting code. Layout rules (see the dataviz guidance this follows):
 *  - one y-axis per panel: `secondary` series (y2, e.g. coverage %) get their
 *    own panel under the main chart, sharing the x-axis and a synced tooltip;
 *  - categorical colors in fixed slot order, assigned by series position;
 *  - legend only for 2+ series; text in text colors, never the series color;
 *  - recessive grid, 2px lines, 4px rounded bar ends, 2px gaps between bars.
 */
import React, { useCallback, useMemo, useRef } from "react";
import { Box, Button, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatValue } from "./formatValue";
import type { ChartAxis, ChartSeries, ChartSpec } from "./types";

/** Validated categorical palette (light surface), slot order fixed. */
export const SERIES_COLORS = [
  "#2a78d6",
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#008300",
  "#6250d6",
  "#e34948",
];
const SURFACE = "#fcfcfb";
const TEXT_PRIMARY = "#0b0b0b";
const TEXT_SECONDARY = "#52514e";
const GRID = "#e6e5e0";

const PRIMARY_HEIGHT = 300;
const SECONDARY_HEIGHT = 170;

export function seriesColor(index: number): string {
  return SERIES_COLORS[index % SERIES_COLORS.length];
}

function renderSeries(s: ChartSeries, color: string, stacked: boolean) {
  const common = {
    key: s.key,
    dataKey: s.key,
    name: s.label,
    isAnimationActive: false,
  };
  if (s.mark === "bar") {
    return (
      <Bar {...common} fill={color} radius={[4, 4, 0, 0]} maxBarSize={48} />
    );
  }
  if (s.mark === "area") {
    return (
      <Area
        {...common}
        type="monotone"
        stroke={color}
        strokeWidth={2}
        fill={color}
        fillOpacity={0.18}
        stackId={stacked ? "area" : undefined}
        connectNulls={false}
      />
    );
  }
  return (
    <Line
      {...common}
      type="monotone"
      stroke={color}
      strokeWidth={2}
      dot={{ r: 4, strokeWidth: 2, stroke: SURFACE, fill: color }}
      activeDot={{ r: 5, strokeWidth: 2, stroke: SURFACE }}
      connectNulls={false}
    />
  );
}

interface PanelProps {
  spec: ChartSpec;
  series: Array<ChartSeries & { color: string }>;
  axis: ChartAxis;
  height: number;
  showXAxisLabel: boolean;
  showLegend: boolean;
}

function ChartPanel({
  spec,
  series,
  axis,
  height,
  showXAxisLabel,
  showLegend,
}: PanelProps) {
  const tooltipFormatter = (value: unknown) =>
    formatValue(value, axis.format, "tooltip");
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart
        data={spec.data}
        syncId={spec.chart_id}
        barGap={2}
        barCategoryGap="20%"
        margin={{ top: 8, right: 16, bottom: showXAxisLabel ? 20 : 4, left: 8 }}
      >
        <CartesianGrid vertical={false} stroke={GRID} />
        <XAxis
          dataKey="x"
          tick={{ fill: TEXT_SECONDARY, fontSize: 12 }}
          tickLine={false}
          axisLine={{ stroke: GRID }}
          label={
            showXAxisLabel && spec.x.label
              ? {
                  value: spec.x.label,
                  position: "insideBottom",
                  offset: -12,
                  fill: TEXT_SECONDARY,
                  fontSize: 12,
                }
              : undefined
          }
        />
        <YAxis
          tickFormatter={(v) => formatValue(v, axis.format, "axis")}
          tick={{ fill: TEXT_SECONDARY, fontSize: 12 }}
          tickLine={false}
          axisLine={false}
          width={64}
          domain={axis.format === "percent" ? ["auto", "auto"] : [0, "auto"]}
          label={
            axis.label
              ? {
                  value: axis.label,
                  angle: -90,
                  position: "insideLeft",
                  fill: TEXT_SECONDARY,
                  fontSize: 12,
                }
              : undefined
          }
        />
        <Tooltip
          formatter={tooltipFormatter}
          contentStyle={{
            borderRadius: 6,
            borderColor: GRID,
            color: TEXT_PRIMARY,
            fontSize: 12,
          }}
          labelStyle={{ color: TEXT_PRIMARY, fontWeight: 600 }}
          cursor={{ fill: "rgba(0,0,0,0.04)", stroke: GRID }}
        />
        {showLegend && (
          <Legend
            verticalAlign="top"
            align="left"
            iconType="circle"
            iconSize={8}
            wrapperStyle={{
              fontSize: 12,
              color: TEXT_SECONDARY,
              paddingBottom: 4,
            }}
            formatter={(label) => (
              <span style={{ color: TEXT_SECONDARY }}>{label}</span>
            )}
          />
        )}
        {series.map((s) => renderSeries(s, s.color, spec.type === "area"))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/** Rasterize the panel SVGs plus title and legend into a PNG and download it. */
async function downloadChartPng(
  host: HTMLElement,
  spec: ChartSpec,
  series: Array<ChartSeries & { color: string }>
) {
  const svgs = Array.from(
    host.querySelectorAll<SVGSVGElement>("svg.recharts-surface")
  );
  if (svgs.length === 0) return;
  const scale = 2;
  const width = Math.max(
    ...svgs.map((s) => s.getBoundingClientRect().width),
    320
  );
  const header = 56;
  const heights = svgs.map((s) => s.getBoundingClientRect().height);
  const canvas = document.createElement("canvas");
  canvas.width = width * scale;
  canvas.height = (header + heights.reduce((a, b) => a + b, 0) + 8) * scale;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.scale(scale, scale);
  ctx.fillStyle = SURFACE;
  ctx.fillRect(0, 0, width, canvas.height / scale);

  ctx.fillStyle = TEXT_PRIMARY;
  ctx.font = "600 15px sans-serif";
  ctx.fillText(spec.title, 12, 22);
  ctx.font = "12px sans-serif";
  let lx = 12;
  for (const s of series) {
    ctx.fillStyle = s.color;
    ctx.beginPath();
    ctx.arc(lx + 4, 40, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = TEXT_SECONDARY;
    ctx.fillText(s.label, lx + 12, 44);
    lx += 24 + ctx.measureText(s.label).width;
  }

  let y = header;
  for (let i = 0; i < svgs.length; i++) {
    const clone = svgs[i].cloneNode(true) as SVGSVGElement;
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const url = URL.createObjectURL(
      new Blob([new XMLSerializer().serializeToString(clone)], {
        type: "image/svg+xml",
      })
    );
    try {
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("chart image failed to load"));
        img.src = url;
      });
      ctx.drawImage(
        img,
        0,
        y,
        svgs[i].getBoundingClientRect().width,
        heights[i]
      );
    } finally {
      URL.revokeObjectURL(url);
    }
    y += heights[i];
  }

  const link = document.createElement("a");
  link.download = `${
    spec.title
      .replace(/[^\w\- ]+/g, "")
      .trim()
      .replace(/\s+/g, "_") || "chart"
  }.png`;
  link.href = canvas.toDataURL("image/png");
  link.click();
}

export default function AnswerChart({ spec }: { spec: ChartSpec }) {
  const hostRef = useRef<HTMLDivElement>(null);

  const colored = useMemo(
    () => spec.series.map((s, i) => ({ ...s, color: seriesColor(i) })),
    [spec]
  );
  const primary = colored.filter((s) => s.axis !== "secondary");
  const secondary = colored.filter((s) => s.axis === "secondary");

  const onDownload = useCallback(() => {
    if (hostRef.current) {
      downloadChartPng(hostRef.current, spec, colored).catch((err) =>
        console.error("[AnswerChart] PNG export failed:", err)
      );
    }
  }, [spec, colored]);

  return (
    <Box
      ref={hostRef}
      role="figure"
      aria-label={spec.title}
      data-testid={`answer-chart-${spec.chart_id}`}
      sx={{
        mt: 1.5,
        mb: 1,
        p: 1.5,
        backgroundColor: SURFACE,
        border: `1px solid ${GRID}`,
        borderRadius: "8px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ color: TEXT_PRIMARY, fontWeight: 600 }}
        >
          {spec.title}
        </Typography>
        <Button
          size="small"
          startIcon={<DownloadIcon fontSize="small" />}
          onClick={onDownload}
          aria-label={`Download chart ${spec.title} as PNG`}
          sx={{ textTransform: "none" }}
        >
          PNG
        </Button>
      </Box>
      {primary.length > 0 && (
        <ChartPanel
          spec={spec}
          series={primary}
          axis={spec.axes.primary}
          height={PRIMARY_HEIGHT}
          showXAxisLabel={secondary.length === 0}
          showLegend={primary.length >= 2 || secondary.length > 0}
        />
      )}
      {secondary.length > 0 && spec.axes.secondary && (
        <ChartPanel
          spec={spec}
          series={secondary}
          axis={spec.axes.secondary}
          height={SECONDARY_HEIGHT}
          showXAxisLabel
          showLegend
        />
      )}
    </Box>
  );
}

/** All charts of one answer, in the order the model created them. */
export function AnswerCharts({ charts }: { charts?: ChartSpec[] }) {
  if (!charts || charts.length === 0) return null;
  return (
    <>
      {charts.map((spec) => (
        <AnswerChart key={spec.chart_id} spec={spec} />
      ))}
    </>
  );
}
